import * as React from "react";
import {
  Card,
  Subtitle1,
  Subtitle2,
  Text,
  Input,
  Button,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Toaster,
  Toast,
  ToastTitle,
  ToastBody,
  useId,
  useToastController,
} from "@fluentui/react-components";
import {
  Add20Regular,
  Delete20Regular,
  Edit20Regular,
  Save20Regular,
  Dismiss20Regular,
  ArrowSync20Regular,
  CheckmarkCircle20Filled,
} from "@fluentui/react-icons";
import {
  type InsuranceClassDto,
  type InsuranceProductDto,
  type SparteMappingDto,
  type OpenBranchDto,
  type Kundenart,
  getTaxonomy,
  listClasses,
  createClass,
  createProduct,
  updateProduct,
  deleteProduct,
  listMapping,
  createMapping,
  updateMapping,
  deleteMapping,
  getOpenBranches,
  applyMapping,
} from "../../services/spartenService";
import { bestaetigen } from "../../components/redesign/bestaetigen";

type ProductGroup = { category: string; products: InsuranceProductDto[] };

// Natives Select mit Kategorie-Gruppen für die Vertragsart-Zuordnung.
const ProductSelect: React.FC<{
  value?: number | null;
  groups: ProductGroup[];
  disabled?: boolean;
  onChange: (id: number | null) => void;
}> = ({ value, groups, disabled, onChange }) => (
  <select
    value={value ?? ""}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    style={{
      minWidth: 240,
      maxWidth: 360,
      padding: "6px 8px",
      borderRadius: 6,
      border: "1px solid #d4cfd9",
      background: "#fff",
      fontSize: 13,
    }}
  >
    <option value="">— nicht zugeordnet —</option>
    {groups.map((g) => (
      <optgroup key={g.category} label={g.category}>
        {g.products.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </optgroup>
    ))}
  </select>
);

const KundenartSelect: React.FC<{
  value: Kundenart;
  onChange: (v: Kundenart) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <select
    value={value ?? ""}
    disabled={disabled}
    onChange={(e) => onChange((e.target.value || null) as Kundenart)}
    style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #d4cfd9", background: "#fff", fontSize: 13 }}
  >
    <option value="">beide</option>
    <option value="FK">FK (Firma)</option>
    <option value="PK">PK (Privat)</option>
  </select>
);

export const SpartenSection: React.FC = () => {
  const toasterId = useId("sparten-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const notify = React.useCallback(
    (title: string, intent: "success" | "error" = "success", body?: string) =>
      dispatchToast(
        <Toast>
          <ToastTitle>{title}</ToastTitle>
          {body ? <ToastBody>{body}</ToastBody> : null}
        </Toast>,
        { intent },
      ),
    [dispatchToast],
  );

  const [classesAll, setClassesAll] = React.useState<InsuranceClassDto[]>([]);
  const [taxonomy, setTaxonomy] = React.useState<InsuranceClassDto[]>([]);
  const [mappings, setMappings] = React.useState<SparteMappingDto[]>([]);
  const [openBranches, setOpenBranches] = React.useState<OpenBranchDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const [tax, cls, maps, open] = await Promise.all([
        getTaxonomy(),
        listClasses(),
        listMapping(),
        getOpenBranches(),
      ]);
      setTaxonomy(tax);
      setClassesAll(cls);
      setMappings(maps);
      setOpenBranches(open);
    } catch (e: any) {
      notify("Laden fehlgeschlagen", "error", String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }, [notify]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Produkt-Gruppen für die Selects (aus der aggregierten Taxonomie).
  const groups: ProductGroup[] = React.useMemo(
    () => taxonomy.map((c) => ({ category: c.insuranceClass, products: c.products ?? [] })),
    [taxonomy],
  );
  const productsByClass = React.useMemo(() => {
    const m = new Map<number, InsuranceProductDto[]>();
    for (const c of taxonomy) m.set(c.id, c.products ?? []);
    return m;
  }, [taxonomy]);

  if (loading) {
    return (
      <Card style={{ padding: 24 }}>
        <Spinner label="Lade Sparten…" />
        <Toaster toasterId={toasterId} position="top-end" />
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Toaster toasterId={toasterId} position="top-end" />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button appearance="secondary" icon={<ArrowSync20Regular />} onClick={loadAll} disabled={busy}>
          Aktualisieren
        </Button>
      </div>

      <TaxonomyCard
        classesAll={classesAll}
        productsByClass={productsByClass}
        busy={busy}
        setBusy={setBusy}
        notify={notify}
        reload={loadAll}
      />

      <MappingCard
        mappings={mappings}
        setMappings={setMappings}
        groups={groups}
        busy={busy}
        setBusy={setBusy}
        notify={notify}
        reload={loadAll}
      />

      <OpenBranchesCard
        openBranches={openBranches}
        groups={groups}
        busy={busy}
        setBusy={setBusy}
        notify={notify}
        reload={loadAll}
      />
    </div>
  );
};

// ---- Taxonomie -------------------------------------------------------------

type Notify = (t: string, i?: "success" | "error", b?: string) => void;

const TaxonomyCard: React.FC<{
  classesAll: InsuranceClassDto[];
  productsByClass: Map<number, InsuranceProductDto[]>;
  busy: boolean;
  setBusy: (b: boolean) => void;
  notify: Notify;
  reload: () => Promise<void>;
}> = ({ classesAll, productsByClass, busy, setBusy, notify, reload }) => {
  const [newCategory, setNewCategory] = React.useState("");
  const [newArt, setNewArt] = React.useState<Record<number, string>>({});
  const [editProductId, setEditProductId] = React.useState<number | null>(null);
  const [editName, setEditName] = React.useState("");

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      await reload();
      notify(ok);
    } catch (e: any) {
      notify("Fehlgeschlagen", "error", String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const activeClasses = classesAll
    .filter((c) => c.active !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.insuranceClass.localeCompare(b.insuranceClass, "de"));
  const maxSort = activeClasses.reduce((mx, c) => Math.max(mx, c.sortOrder ?? 0), 0);

  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Subtitle1>Vertragskategorien & -arten</Subtitle1>
          <Text size={200} style={{ color: "#6b5a66" }}>
            Die KMVS-Taxonomie, die im Verträge-Tab und beim Mapping verwendet wird.
          </Text>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Input
            placeholder="Neue Kategorie"
            value={newCategory}
            onChange={(_, v) => setNewCategory(v.value)}
            style={{ minWidth: 220 }}
          />
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            disabled={busy || !newCategory.trim()}
            onClick={() =>
              run(async () => {
                await createClass({ insuranceClass: newCategory.trim(), sortOrder: maxSort + 10, active: true });
                setNewCategory("");
              }, "Kategorie angelegt")
            }
          >
            Kategorie
          </Button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {activeClasses.map((c) => {
          const products = (productsByClass.get(c.id) ?? []).filter((p) => p.active !== false);
          const draft = newArt[c.id] ?? "";
          return (
            <div key={c.id} style={{ border: "1px solid #ECE7F0", borderRadius: 10, padding: "10px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <Subtitle2>
                  {c.insuranceClass}{" "}
                  <Text size={200} style={{ color: "#a294a0" }}>
                    ({products.length})
                  </Text>
                </Subtitle2>
                <div style={{ display: "flex", gap: 6 }}>
                  <Input
                    placeholder="Vertragsart hinzufügen"
                    value={draft}
                    onChange={(_, v) => setNewArt((p) => ({ ...p, [c.id]: v.value }))}
                    style={{ minWidth: 240 }}
                  />
                  <Button
                    size="small"
                    appearance="primary"
                    icon={<Add20Regular />}
                    disabled={busy || !draft.trim()}
                    onClick={() =>
                      run(async () => {
                        await createProduct({
                          insuranceClassID: c.id,
                          name: draft.trim(),
                          isRelevantForPerson: false,
                          isRelevantForCompany: true,
                          sortOrder: (products.reduce((mx, p) => Math.max(mx, p.sortOrder ?? 0), 0) || 0) + 10,
                          active: true,
                        });
                        setNewArt((p) => ({ ...p, [c.id]: "" }));
                      }, "Vertragsart angelegt")
                    }
                  >
                    Art
                  </Button>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                {products.map((p) =>
                  editProductId === p.id ? (
                    <div key={p.id} style={{ display: "flex", gap: 6, alignItems: "center", paddingLeft: 4 }}>
                      <Input value={editName} onChange={(_, v) => setEditName(v.value)} style={{ flex: 1 }} />
                      <Button
                        size="small"
                        appearance="primary"
                        icon={<Save20Regular />}
                        disabled={busy || !editName.trim()}
                        onClick={() =>
                          run(async () => {
                            await updateProduct(p.id, { name: editName.trim() });
                            setEditProductId(null);
                          }, "Gespeichert")
                        }
                      />
                      <Button size="small" icon={<Dismiss20Regular />} onClick={() => setEditProductId(null)} />
                    </div>
                  ) : (
                    <div key={p.id} style={{ display: "flex", gap: 8, alignItems: "center", paddingLeft: 4 }}>
                      <span style={{ color: "#a294a0" }}>•</span>
                      <Text style={{ flexGrow: 1 }}>
                        {p.name}
                        {p.detail ? (
                          <Text size={200} style={{ color: "#a294a0" }}>
                            {"  ·  "}
                            {p.detail}
                          </Text>
                        ) : null}
                      </Text>
                      {p.isRelevantForPerson && !p.isRelevantForCompany ? (
                        <Badge appearance="tint" color="informative">
                          PK
                        </Badge>
                      ) : null}
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Edit20Regular />}
                        title="Umbenennen"
                        onClick={() => {
                          setEditProductId(p.id);
                          setEditName(p.name);
                        }}
                      />
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Delete20Regular />}
                        title="Deaktivieren / löschen"
                        disabled={busy}
                        onClick={async () => {
                          const ok = await bestaetigen({
                            titel: "Vertragsart entfernen?",
                            text: `„${p.name}" wird entfernt. Hängen schon Verträge daran, wird die Vertragsart nur deaktiviert.`,
                            ja: "Entfernen",
                            ton: "danger",
                          });
                          if (!ok) return;
                          void run(() => deleteProduct(p.id), "Entfernt");
                        }}
                      />
                    </div>
                  ),
                )}
                {products.length === 0 ? (
                  <Text size={200} style={{ color: "#a294a0", paddingLeft: 4 }}>
                    Noch keine Vertragsart.
                  </Text>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// ---- Mapping ---------------------------------------------------------------

const MappingCard: React.FC<{
  mappings: SparteMappingDto[];
  setMappings: React.Dispatch<React.SetStateAction<SparteMappingDto[]>>;
  groups: ProductGroup[];
  busy: boolean;
  setBusy: (b: boolean) => void;
  notify: Notify;
  reload: () => Promise<void>;
}> = ({ mappings, setMappings, groups, busy, setBusy, notify, reload }) => {
  const [search, setSearch] = React.useState("");
  const [kFilter, setKFilter] = React.useState<"all" | "FK" | "PK">("all");

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return mappings.filter(
      (m) =>
        (!q || m.rawVertragsart.toLowerCase().includes(q)) &&
        (kFilter === "all" || (m.kundenart ?? "") === kFilter),
    );
  }, [mappings, search, kFilter]);

  const patch = async (id: number, p: Partial<SparteMappingDto>) => {
    setBusy(true);
    try {
      await updateMapping(id, p);
      setMappings((prev) => prev.map((m) => (m.id === id ? { ...m, ...p } : m)));
    } catch (e: any) {
      notify("Speichern fehlgeschlagen", "error", String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const onApply = async () => {
    setBusy(true);
    try {
      const res = await applyMapping(false);
      notify("Mapping angewendet", "success", `${res?.updated ?? 0} Verträge zugeordnet.`);
      await reload();
    } catch (e: any) {
      notify("Anwenden fehlgeschlagen", "error", String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const openCount = mappings.filter((m) => m.insuranceProductID == null).length;

  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Subtitle1>Mapping: importierte Vertragsart → KMVS-Vertragsart</Subtitle1>
          <Text size={200} style={{ color: "#6b5a66" }}>
            {mappings.length} Regeln{openCount > 0 ? ` · ${openCount} ohne Zuordnung` : ""}.
          </Text>
        </div>
        <Button appearance="primary" icon={<CheckmarkCircle20Filled />} onClick={onApply} disabled={busy}>
          Mapping auf Verträge anwenden
        </Button>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Input placeholder="Suche Produktgeber-Vertragsart…" value={search} onChange={(_, v) => setSearch(v.value)} style={{ minWidth: 260 }} />
        {(["all", "FK", "PK"] as const).map((k) => (
          <Button key={k} size="small" appearance={kFilter === k ? "primary" : "secondary"} onClick={() => setKFilter(k)}>
            {k === "all" ? "Alle" : k}
          </Button>
        ))}
      </div>

      <div style={{ maxHeight: "48vh", overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <Th>Produktgeber-Vertragsart</Th>
              <Th>Kundenart</Th>
              <Th>KMVS-Vertragsart</Th>
              <Th>Aktiv</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} style={{ borderBottom: "1px solid #f1edf3" }}>
                <Td>{m.rawVertragsart}</Td>
                <Td>{m.kundenart ?? "—"}</Td>
                <Td>
                  <ProductSelect
                    value={m.insuranceProductID ?? null}
                    groups={groups}
                    disabled={busy}
                    onChange={(id) => patch(m.id, { insuranceProductID: id })}
                  />
                  {m.insuranceProductID == null ? (
                    <Badge appearance="tint" color="warning" style={{ marginLeft: 8 }}>
                      offen
                    </Badge>
                  ) : null}
                </Td>
                <Td>
                  <input type="checkbox" checked={m.active !== false} onChange={(e) => patch(m.id, { active: e.target.checked })} disabled={busy} />
                </Td>
                <Td>
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<Delete20Regular />}
                    disabled={busy}
                    onClick={async () => {
                      const ok = await bestaetigen({
                        titel: "Zuordnung löschen?",
                        text: `Die Zuordnung für „${m.rawVertragsart}" wird gelöscht.`,
                        ja: "Löschen",
                        ton: "danger",
                      });
                      if (!ok) return;
                      setBusy(true);
                      deleteMapping(m.id)
                        .then(() => setMappings((prev) => prev.filter((x) => x.id !== m.id)))
                        .catch((e) => notify("Löschen fehlgeschlagen", "error", String(e?.message ?? e)))
                        .finally(() => setBusy(false));
                    }}
                  />
                </Td>
              </tr>
            ))}
            {filtered.length === 0 ? (
              <tr>
                <Td colSpan={5}>
                  <Text style={{ color: "#a294a0" }}>Keine Treffer.</Text>
                </Td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ---- Offene Roh-Vertragsarten ---------------------------------------------

const OpenBranchesCard: React.FC<{
  openBranches: OpenBranchDto[];
  groups: ProductGroup[];
  busy: boolean;
  setBusy: (b: boolean) => void;
  notify: Notify;
  reload: () => Promise<void>;
}> = ({ openBranches, groups, busy, setBusy, notify, reload }) => {
  const [drafts, setDrafts] = React.useState<Record<string, { productId: number | null; kundenart: Kundenart }>>({});

  const getDraft = (b: string) => drafts[b] ?? { productId: null, kundenart: null };
  const setDraft = (b: string, p: Partial<{ productId: number | null; kundenart: Kundenart }>) =>
    setDrafts((prev) => ({ ...prev, [b]: { ...getDraft(b), ...p } }));

  const assign = async (branch: string) => {
    const d = getDraft(branch);
    if (d.productId == null) {
      notify("Bitte zuerst eine KMVS-Vertragsart wählen", "error");
      return;
    }
    setBusy(true);
    try {
      await createMapping({ rawVertragsart: branch, kundenart: d.kundenart, insuranceProductID: d.productId });
      notify("Zuordnung angelegt", "success", "Über „Mapping anwenden“ wirken sie auf die Verträge.");
      await reload();
    } catch (e: any) {
      notify("Anlegen fehlgeschlagen", "error", String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Subtitle1>Offene importierte Vertragsarten</Subtitle1>
        <Text size={200} style={{ color: "#6b5a66" }}>
          Roh-Vertragsarten aus den Verträgen dieses Mandanten, die noch keiner KMVS-Vertragsart zugeordnet sind.
        </Text>
      </div>

      {openBranches.length === 0 ? (
        <MessageBar intent="success">
          <MessageBarBody>Alle importierten Vertragsarten sind zugeordnet. 🎉</MessageBarBody>
        </MessageBar>
      ) : (
        <div style={{ maxHeight: "40vh", overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <Th>Roh-Vertragsart</Th>
                <Th>Verträge</Th>
                <Th>Kundenart</Th>
                <Th>KMVS-Vertragsart</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {openBranches.map((b) => {
                const d = getDraft(b.branch);
                return (
                  <tr key={b.branch} style={{ borderBottom: "1px solid #f1edf3" }}>
                    <Td>{b.branch}</Td>
                    <Td>{b.count}</Td>
                    <Td>
                      <KundenartSelect value={d.kundenart} onChange={(v) => setDraft(b.branch, { kundenart: v })} disabled={busy} />
                    </Td>
                    <Td>
                      <ProductSelect value={d.productId} groups={groups} disabled={busy} onChange={(id) => setDraft(b.branch, { productId: id })} />
                    </Td>
                    <Td>
                      <Button size="small" appearance="primary" disabled={busy} onClick={() => assign(b.branch)}>
                        Zuordnen
                      </Button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// ---- kleine Tabellen-Primitiven -------------------------------------------

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th
    style={{
      position: "sticky",
      top: 0,
      background: "#faf8fc",
      textAlign: "left",
      padding: "8px 10px",
      borderBottom: "1px solid #e6e0ec",
      fontWeight: 600,
      whiteSpace: "nowrap",
      zIndex: 1,
    }}
  >
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; colSpan?: number }> = ({ children, colSpan }) => (
  <td colSpan={colSpan} style={{ padding: "6px 10px", verticalAlign: "middle" }}>
    {children}
  </td>
);

export default SpartenSection;
